// @ts-strict-ignore
import Descriptions from 'antd/lib/descriptions';
import React from 'react';
import {
    appleReleaseTypeForReleaseMethod,
    releaseMethodForAppleReleaseType,
    releaseSettingsNeedPhasedReleaseWarning,
} from '../../helpers';
import { displayVersionReleaseType } from '../../helpers/displayHelpers';
import {
    AppleAppStoreDefaultSettings,
    AppleAppStoreData,
    ReleaseStepSubmissionData,
    ReleaseStepType,
} from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import { WarningFilled, CheckCircleFilled } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { useAppPlatform } from '@/utils/hooks/helpers';
import { Tooltip } from 'antd/lib';
import { RootState } from '@/reducers/reducers';
import { selectRelease, selectReleaseStep } from '@/selectors/selectors';
import { useSelector } from 'react-redux';

interface AppleAppStoreReleaseSettingsDescriptionsProps extends ReleaseComponentProps {
    appDefaultSettings?: AppleAppStoreDefaultSettings;
    appStoreReleaseSettings?: AppleAppStoreDefaultSettings;
    appStoreData?: AppleAppStoreData;
    isRollback: boolean;
    isRunwayReleaseAutomationInactiveBecauseOfApple: boolean;
    willBeReleasedByRunway: boolean;
    labelStyle?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
    colon?: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    releaseStepType?: ReleaseStepType;
}

const AppleAppStoreReleaseSettingsDescriptions: React.FC<AppleAppStoreReleaseSettingsDescriptionsProps> = (
    props: AppleAppStoreReleaseSettingsDescriptionsProps,
) => {
    const {
        releaseStepType,
        appStoreData,
        appStoreReleaseSettings,
        willBeReleasedByRunway,
        isRunwayReleaseAutomationInactiveBecauseOfApple,
    } = props;

    const platform = useAppPlatform(props);
    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId));
    const releaseStep =
        releaseStepType &&
        useSelector((state: RootState) => selectReleaseStep(state, props.releaseId, releaseStepType));
    const stepData = releaseStep?.data as ReleaseStepSubmissionData;

    let isPhasedRelease = false;
    let releaseType = null;
    let releaseMethod = null;
    let earliestReleaseDate = null;

    if (appStoreData || appStoreReleaseSettings) {
        isPhasedRelease =
            appStoreData ? !!appStoreData.phasedRelease
            : appStoreReleaseSettings ? (appStoreReleaseSettings as AppleAppStoreDefaultSettings).phasedRelease
            : false;
        releaseType =
            appStoreData ? appStoreData.versionReviewSubmissionItem?.releaseType
            : appStoreReleaseSettings ?
                appleReleaseTypeForReleaseMethod(
                    (appStoreReleaseSettings as AppleAppStoreDefaultSettings).releaseMethod,
                )
            :   null;
        releaseMethod =
            appStoreData?.versionReviewSubmissionItem ?
                releaseMethodForAppleReleaseType(appStoreData.versionReviewSubmissionItem.releaseType)
            : appStoreReleaseSettings ? (appStoreReleaseSettings as AppleAppStoreDefaultSettings).releaseMethod
            : null;
    }

    earliestReleaseDate = appStoreData ? appStoreData.versionReviewSubmissionItem?.earliestReleaseDate : null;

    let phasedReleaseText =
        isPhasedRelease ? 'On (release will be phased over 7 days)' : 'Off (release update to all users immediately)';

    const phasedReleaseSettingIsDifferent = !!appStoreData?.phasedRelease != appStoreReleaseSettings?.phasedRelease;
    const releaseMethodSettingIsDifferent = releaseMethod != appStoreReleaseSettings?.releaseMethod;

    // warn if app release phased settings differ from app default phased settings
    const needsPhasedReleaseWarning = releaseSettingsNeedPhasedReleaseWarning(
        platform,
        props.isRollback,
        props.appDefaultSettings,
        appStoreReleaseSettings,
    );

    const GetAppStoreSettingsIsDifferentTooltip = () => {
        if (releaseStepType !== ReleaseStepType.Submission) {
            return null;
        }

        let tooltipText = null;
        if (release?.submittedAt || stepData?.submittedAt) {
            tooltipText =
                'The values in App Store Connect are different from your configured settings in Runway. Runway cannot update App Store Connect settings after the app update has been submitted for review.';
        } else if (release?.setManualReleaseTypeForAutoRelease || stepData?.setManualReleaseTypeForAutoRelease) {
            tooltipText =
                'Runway updated these values in App Store Connect once to match your configured settings, but they were manually changed again. To avoid conflicts, Runway will not re-attempt to update.';
        } else {
            tooltipText =
                'The values in App Store Connect are different from those stored in your default App Store Connect settings. We will try to apply your configured settings within 5 - 10 minutes.';
        }

        return (
            <Tooltip title={tooltipText}>
                <WarningFilled style={{ color: runwayColors.statusYellow, marginLeft: 8 }} />
            </Tooltip>
        );
    };

    return (
        <Descriptions colon={props.colon} column={1} labelStyle={props.labelStyle} contentStyle={props.contentStyle}>
            <Descriptions.Item label="Version release">
                {releaseType ?
                    <Space direction="vertical">
                        <div>
                            {displayVersionReleaseType(releaseType, earliestReleaseDate)}
                            {releaseMethodSettingIsDifferent && GetAppStoreSettingsIsDifferentTooltip()}
                        </div>
                        {isRunwayReleaseAutomationInactiveBecauseOfApple && (
                            <Space align="start">
                                <WarningFilled style={{ color: runwayColors.statusYellow }} />
                                <div>
                                    <Text style={{ color: runwayColors.statusYellow, fontWeight: 600 }}>
                                        App Store Connect override:{' '}
                                    </Text>
                                    <Text style={{ color: antGrey.grey7 }}>
                                        Runway is unable to perform your scheduled release automation because the
                                        version release setting is not set to Manual. Change this setting to Manual to
                                        allow Runway to release automatically as expected.
                                    </Text>
                                </div>
                            </Space>
                        )}
                        {willBeReleasedByRunway && !isRunwayReleaseAutomationInactiveBecauseOfApple && (
                            <Space align="start">
                                <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                                <Text style={{ color: antGrey.grey7 }}>
                                    Keep this set to Manual in App Store Connect in order for Runway to run your release
                                    automation.
                                </Text>
                            </Space>
                        )}
                    </Space>
                :   <Text type="secondary">Not set </Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Phased release">
                <Space direction="vertical">
                    <div>
                        {phasedReleaseText ? phasedReleaseText : <Text type="secondary">Not set</Text>}
                        {phasedReleaseSettingIsDifferent && GetAppStoreSettingsIsDifferentTooltip()}
                    </div>
                    {needsPhasedReleaseWarning && (
                        <Space align="start">
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                            <Text style={{ color: antGrey.grey7 }}>
                                Phased release is off for this release because it&apos;s a rollback.
                            </Text>
                        </Space>
                    )}
                </Space>
            </Descriptions.Item>
        </Descriptions>
    );
};

export default AppleAppStoreReleaseSettingsDescriptions;
